import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, CheckCircle2, MinusCircle, PlayCircle, Link2, Building2, Wrench, Users } from "lucide-react";

const TIPO = {
  empresa: { label: "Empresa", icon: Building2, cls: "bg-aj-blue-deep text-white" },
  montador: { label: "Montador", icon: Wrench, cls: "bg-gradient-to-r from-[#FF6700] to-[#F97316] text-white" },
  montadora: { label: "Montadora", icon: Users, cls: "bg-emerald-600 text-white" },
};
const ST = {
  aberta: { label: "Aberta", cls: "bg-aj-amber-soft text-aj-orange-hover" },
  paga: { label: "Paga", cls: "bg-aj-success text-white" },
  isenta: { label: "Isenta", cls: "bg-aj-surface-2 text-aj-text-muted" },
};
const MOTIVO = {
  gratuidade_6_meses: "6 meses grátis",
  sem_atendimentos_no_mes: "Sem atendimentos",
  faturamento_abaixo_de_150: "Faturou ≤ R$150",
  sem_receita_no_mes: "Sem receita",
};

const mesAnterior = () => {
  const d = new Date(); d.setDate(1); d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function FaturamentoSaas() {
  const [periodo, setPeriodo] = useState(mesAnterior());
  const [data, setData] = useState(null);
  const [gerando, setGerando] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/saas/faturas", { params: { periodo } })
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)));
  }, [periodo]);
  useEffect(load, [load]);

  const gerar = async () => {
    setGerando(true);
    try {
      const r = await api.post(`/admin/saas/gerar-faturas?periodo=${periodo}`);
      const c = r.data.criadas;
      toast.success(`Geradas: ${c.empresa} empresa(s), ${c.montador} montador(es), ${c.montadora} montadora(s), ${c.isentas} isenta(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGerando(false); }
  };

  const gerarLink = async (f) => {
    try {
      const r = await api.post(`/saas/faturas/${f.id}/checkout`, { origin_url: window.location.origin + "/login" });
      await navigator.clipboard.writeText(r.data.checkout_url);
      toast.success("Link de pagamento copiado! Envie para o cobrado.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Financeiro SaaS</div>
          <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Faturamento SaaS</h1>
          <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
            Empresas: mensalidade por faixa + R$ 1,50/atendimento · Montadores: R$ 29,99 após 6 meses grátis
            (isento se não trabalhou ou faturou ≤ R$ 150) · Montadoras: 5% da receita, 6 meses grátis.
            Geração automática todo dia 1º.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted mb-1.5">Período</div>
            <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)}
              className="rounded-lg w-[160px]" data-testid="fs-periodo" />
          </div>
          <button onClick={gerar} disabled={gerando} className="btn-aj-primary text-sm disabled:opacity-60" data-testid="fs-gerar">
            <PlayCircle size={16} /> {gerando ? "Gerando…" : "Gerar faturas"}
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={<Wallet size={18} />} label="A receber" v={`R$ ${data.resumo.a_receber.toFixed(2)}`} destaque testid="fs-areceber" />
          <Kpi icon={<CheckCircle2 size={18} />} label="Recebido" v={`R$ ${data.resumo.recebido.toFixed(2)}`} testid="fs-recebido" />
          <Kpi icon={<MinusCircle size={18} />} label="Isentas" v={data.resumo.isentas} testid="fs-isentas" />
          <Kpi icon={<Wallet size={18} />} label="Faturas" v={data.resumo.total} testid="fs-total" />
        </div>
      )}

      <div className="aj-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-aj-surface-2 border-b border-[#E2E8F0]">
            <tr><Th>Tipo</Th><Th>Nome</Th><Th>Base de cálculo</Th><Th>Valor</Th><Th>Status</Th><Th>Ação</Th></tr>
          </thead>
          <tbody>
            {(!data || data.faturas.length === 0) && (
              <tr><td colSpan={6} className="p-10 text-center text-aj-text-muted">
                Nenhuma fatura no período. Clique em "Gerar faturas".
              </td></tr>
            )}
            {data?.faturas.map(f => {
              const t = TIPO[f.tipo] || TIPO.empresa;
              const s = ST[f.status] || ST.aberta;
              return (
                <tr key={f.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-aj-surface transition-colors" data-testid={`fs-row-${f.id}`}>
                  <Td>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${t.cls}`}>
                      <t.icon size={11} /> {t.label}
                    </span>
                  </Td>
                  <Td className="font-semibold">{f.nome}</Td>
                  <Td className="text-xs text-aj-text-muted">
                    {f.tipo === "empresa" && <>{f.qtd_atendimentos} atend. · faixa R$ {f.mensalidade?.toFixed(2)} + R$ {f.variavel?.toFixed(2)}</>}
                    {f.tipo === "montador" && <>{f.atendimentos_mes} atend. · faturou R$ {f.faturamento_mes?.toFixed(2)}</>}
                    {f.tipo === "montadora" && <>receita R$ {f.receita_mes?.toFixed(2)} × {f.percentual}%</>}
                    {f.motivo_isencao && <span className="block text-aj-orange font-semibold">{MOTIVO[f.motivo_isencao] || f.motivo_isencao}</span>}
                  </Td>
                  <Td className="font-mono font-bold">{f.valor_total > 0 ? `R$ ${f.valor_total.toFixed(2)}` : "—"}</Td>
                  <Td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${s.cls}`}>{s.label}</span></Td>
                  <Td>
                    {f.status === "aberta" && (
                      <button onClick={() => gerarLink(f)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-aj-orange hover:underline"
                        data-testid={`fs-link-${f.id}`}>
                        <Link2 size={12} /> Link de pagamento
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon, label, v, destaque, testid }) {
  return (
    <div className={`aj-card p-5 ${destaque ? "border-l-4 border-l-[#FF6700]" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-aj-orange">{icon}
        <span className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</span>
      </div>
      <div className="font-display font-extrabold text-2xl text-aj-blue-deep mt-2">{v}</div>
    </div>
  );
}
function Th({ children }) { return <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
